import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatDateString, formatter } from 'app/main/apps/common/AuraFunctions';
import { getPaymentHistory } from 'app/main/apps/e-commerce/store/ordersSlice';
import PaymentReversalDialog from './PaymentReversalDialog';
import { canReverseRow } from './paymentReversalScope';

/**
 * PGSS-5544 — what has been paid against this invoice, on the invoice itself.
 *
 * Invoice Details previously showed the invoice and its lines but no payments, so correcting a
 * returned payment meant closing the screen and reopening a different popup from the row menu.
 *
 * Kept as its own component on purpose. ViewOrderDialog is past two thousand lines, and PGSS-5541
 * was caused by adding one more declaration into it; a panel that owns its own fetching and state
 * adds one line there instead of ten.
 *
 * It holds its results locally rather than reading the shared `paymentHistory` slot, so opening an
 * invoice cannot disturb the Payment History view or be disturbed by it.
 */
function InvoicePaymentsPanel({ invoiceId, canReverse = false, onReversed }) {
  const dispatch = useDispatch();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentToReverse, setPaymentToReverse] = useState(null);

  const load = useCallback(() => {
    if (!invoiceId) {
      return;
    }
    setLoading(true);
    dispatch(getPaymentHistory(invoiceId))
      .unwrap()
      .then((rows) => setPayments(Array.isArray(rows) ? rows : []))
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }, [dispatch, invoiceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!invoiceId) {
    return null;
  }

  const amountOf = (row) => formatter.format(row.paid_amount ?? row.amount ?? 0);

  return (
    <div className="print:hidden mb-16">
      <Typography variant="subtitle1" className="font-semibold mb-8">
        Payments
      </Typography>

      {loading ? (
        <Typography variant="body2" color="textSecondary">
          Loading payments…
        </Typography>
      ) : payments.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No payments have been recorded against this invoice.
        </Typography>
      ) : (
        <Table size="small" className="simple w-full">
          <TableHead>
            <TableRow>
              <TableCell className="font-semibold">Type</TableCell>
              <TableCell className="font-semibold">Amount</TableCell>
              <TableCell className="font-semibold">Notes</TableCell>
              <TableCell className="font-semibold">Date</TableCell>
              {canReverse && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((row, index) => (
              <TableRow key={row.id ?? index}>
                <TableCell>{row.payment_type || '-'}</TableCell>
                <TableCell>{amountOf(row)}</TableCell>
                <TableCell>
                  {row.description ? (
                    <Tooltip title={row.description}>
                      <Typography style={{ whiteSpace: 'nowrap' }}>
                        {row.description.length > 60
                          ? `${row.description.slice(0, 60)}…`
                          : row.description}
                      </Typography>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{row.date ? formatDateString(row.date) : '-'}</TableCell>
                {canReverse && (
                  <TableCell>
                    {/* The server decides. A returned payment keeps its original positive row so the
                        ledger shows both halves, so nothing visible here distinguishes it. */}
                    {canReverseRow(row) ? (
                      <Button size="small" color="secondary" onClick={() => setPaymentToReverse(row)}>
                        Reverse
                      </Button>
                    ) : null}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PaymentReversalDialog
        open={!!paymentToReverse}
        payment={paymentToReverse}
        invoiceId={paymentToReverse?.invoice_id ?? invoiceId}
        onClose={() => setPaymentToReverse(null)}
        onReversed={() => {
          // Refresh our own list, then let the caller refresh whatever sits behind us -- the
          // invoice row's paid and balance figures have moved.
          load();
          if (onReversed) {
            onReversed();
          }
        }}
      />
    </div>
  );
}

export default InvoicePaymentsPanel;
